import React, { useState } from 'react';
import { Lock, Loader, AlertTriangle } from 'lucide-react';
import { verifyUserCredentials } from '../services/storage';

interface SecureActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  actionButtonText?: string;
}

const SecureActionModal: React.FC<SecureActionModalProps> = ({ 
  isOpen, onClose, onConfirm, title, message, actionButtonText = "Confirm Delete" 
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Please enter both username and password.");
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyUserCredentials(username, password);
      // If verification succeeds, proceed with the action
      await onConfirm();
      onClose(); // Close on success
      // Reset fields
      setUsername('');
      setPassword('');
    } catch (err: any) {
      setError(err.message || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-red-50 p-4 border-b border-red-100 flex items-center">
          <div className="bg-red-100 p-2 rounded-full mr-3 text-red-600">
            <Lock size={20} />
          </div>
          <h3 className="text-lg font-bold text-red-800">{title}</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <p className="text-gray-600 mb-6 flex items-start text-sm">
            <AlertTriangle size={16} className="text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
            {message}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Username</label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded p-2.5 focus:ring-2 focus:ring-red-500 outline-none"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input 
                type="password" 
                className="w-full border border-gray-300 rounded p-2.5 focus:ring-2 focus:ring-red-500 outline-none"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 text-red-600 text-sm p-3 rounded border border-red-200">
              {error}
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-bold shadow-sm flex justify-center items-center"
            >
              {loading ? <Loader className="animate-spin" size={18} /> : actionButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecureActionModal;